import { useCallback, useEffect, useRef, useState } from 'react';

export interface UseIdleTimerOptions {
  idleThresholdMs: number;
  gracePeriodMs: number;
  onTimeout: () => void;
}

export interface UseIdleTimerResult {
  showPrompt: boolean;
  countdown: number;
  resetTimer: () => void;
}

const ACTIVITY_EVENTS: ReadonlyArray<keyof WindowEventMap> = [
  'mousemove',
  'mousedown',
  'keydown',
  'scroll',
  'touchstart',
  // 'blur' fires on the parent window when focus moves into an iframe (e.g. the
  // Amazon Connect chat widget). Without this, chat interactions are invisible
  // to the idle timer and the session can expire while a user is in the chat.
  'blur',
];

/**
 * Detects user inactivity and manages a grace-period countdown before timeout.
 *
 * After idleThresholdMs of no user activity, showPrompt becomes true and
 * countdown begins decreasing (in seconds) from gracePeriodMs / 1000.
 * When countdown reaches 0, onTimeout is called.
 *
 * Calling resetTimer() hides the prompt and restarts the idle clock.
 */
export const useIdleTimer = ({
  idleThresholdMs,
  gracePeriodMs,
  onTimeout,
}: UseIdleTimerOptions): UseIdleTimerResult => {
  const initialCountdown = Math.ceil(gracePeriodMs / 1000);
  const [showPrompt, setShowPrompt] = useState(false);
  const [countdown, setCountdown] = useState(initialCountdown);

  const lastActivityRef = useRef(Date.now());
  const showPromptRef = useRef(false);
  const countdownRef = useRef(initialCountdown);
  const timeoutAtRef = useRef<number | null>(null);
  const hasTimedOutRef = useRef(false);
  const onTimeoutRef = useRef(onTimeout);

  // Keep onTimeout ref current so the interval closure doesn't go stale
  useEffect(() => {
    onTimeoutRef.current = onTimeout;
  }, [onTimeout]);

  const resetTimer = useCallback(() => {
    const nextCountdown = Math.ceil(gracePeriodMs / 1000);
    lastActivityRef.current = Date.now();
    showPromptRef.current = false;
    countdownRef.current = nextCountdown;
    timeoutAtRef.current = null;
    hasTimedOutRef.current = false;
    setShowPrompt(false);
    setCountdown(nextCountdown);
  }, [gracePeriodMs]);

  // Record activity; reset prompt if it hasn't appeared yet
  useEffect(() => {
    const handleActivity = () => {
      lastActivityRef.current = Date.now();
      if (!showPromptRef.current) {
        // Reset countdown silently so it's fresh when the prompt next appears
        countdownRef.current = Math.ceil(gracePeriodMs / 1000);
        timeoutAtRef.current = null;
      }
    };

    ACTIVITY_EVENTS.forEach((event) =>
      globalThis.addEventListener(event, handleActivity, { passive: true }),
    );

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        globalThis.removeEventListener(event, handleActivity),
      );
    };
  }, [gracePeriodMs]);

  // Single 1-second interval: check idle then tick countdown
  useEffect(() => {
    const interval = setInterval(() => {
      // If an iframe (e.g. the Amazon Connect chat widget) is the active
      // element, treat each tick as activity. blur only fires once when focus
      // first enters the iframe, so without this the idle clock would expire
      // while the user is actively chatting.
      if (document.activeElement?.tagName === 'IFRAME') {
        lastActivityRef.current = Date.now();
      }

      if (!showPromptRef.current) {
        const idle = Date.now() - lastActivityRef.current;
        if (idle >= idleThresholdMs) {
          const elapsedGraceMs = idle - idleThresholdMs;
          const remainingGraceMs = Math.max(0, gracePeriodMs - elapsedGraceMs);
          const nextCountdown = Math.ceil(remainingGraceMs / 1000);

          showPromptRef.current = true;
          setShowPrompt(true);

          if (remainingGraceMs <= 0 && !hasTimedOutRef.current) {
            countdownRef.current = 0;
            timeoutAtRef.current = Date.now();
            setCountdown(0);
            hasTimedOutRef.current = true;
            onTimeoutRef.current();
            return;
          }

          countdownRef.current = nextCountdown;
          timeoutAtRef.current = Date.now() + remainingGraceMs;
          setCountdown(nextCountdown);
        }
        return;
      }

      const timeoutAt = timeoutAtRef.current;
      if (timeoutAt === null) {
        return;
      }

      const remainingMs = Math.max(0, timeoutAt - Date.now());
      const next = Math.ceil(remainingMs / 1000);

      if (next !== countdownRef.current) {
        countdownRef.current = next;
        setCountdown(next);
      }

      if (remainingMs <= 0 && !hasTimedOutRef.current) {
        hasTimedOutRef.current = true;
        onTimeoutRef.current();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [gracePeriodMs, idleThresholdMs]);

  return { showPrompt, countdown, resetTimer };
};
